import { RUN_MODE } from "../../common/utility"
import { BreakpointMap } from "../../common/breakpoint"
import {
  handleGetBreakpoints,
  handleCanGoBackward,
  handleCanGoForward,
  handleGetC800Slot,
  handleGetExecution,
  handleGetIsDebugging,
  handleGetMachineName,
  handleGetMemSize,
  handleGetRunMode,
  handleGetSaveState,
  handleGetShowDebugTab,
  handleGetSoftSwitches,
  handleGetSpeedMode,
  handleGetStackString,
  handleGetState6502,
  handleGetTextPageAsString,
  handleGetTempStateIndex,
  handleGetTimeTravelThumbnails,
  passGoBackInTime,
  passGoForwardInTime,
  passAppleCommandKeyPress,
  passAppleCommandKeyRelease,
  passKeyRelease,
  passKeypress,
  passMouseEvent,
  passPasteText,
  passSetIsDebugging,
  passSetBinaryBlock,
  requestSetState6502,
  passSetMemory,
  requestWriteMemory,
  passSetSoftSwitches,
  passStepInto,
  passStepOut,
  passStepOver,
  passTimeTravelIndex,
  passTimeTravelSnapshot,
  passSetShowDebugTab,
  requestKeyboardState,
  requestKeySequence,
  requestConditionalKeySequence,
  requestCancelConditionalKeySequence,
  passSetRunMode,
  requestSetRunMode,
  requestLoadBinary,
  requestCreateSessionSnapshot,
  requestSessionMemoryComparison,
  requestRestoreSessionSnapshot,
  setExecutionStateCallback,
  handleGetCurrentMemory,
} from "../main2worker"
import { getBinaryLoadError, runBinary } from "../binaryload"
import {
  setPreferenceBreakpoints,
  setPreferenceColorMode,
  setPreferenceMachineName,
  setPreferenceRamWorks,
  requestPreferenceSpeedMode,
  setPreferenceMonitorMode,
} from "../localstorage"
import { RestoreSaveState } from "../savestate"
import { getUIState } from "../ui_settings"
import { getMockingboardMode } from "../devices/audio/mockingboard_audio"
import { isAudioEnabled } from "../devices/audio/speaker"
import {
  handleGetFilename,
  handleGetDriveProps,
  handleSetDiskWriteProtected,
  requestEjectDisk,
  requestSetDiskFromURL,
  requestMountDiskFromBuffer,
} from "../devices/disk/driveprops"
import { parseRemoteKeyboardState } from "./remotecontrol_input"
import { captureRenderedScreen } from "./remotecontrol_screen"
import {
  clearRemoteMemoryWriteWatchpoint,
  findRemoteMemory,
  readRemoteMemory,
  setRemoteMemoryWriteWatchpoint,
} from "./remotecontrol_memory"

const CONNECT_RETRY_MS = 3000
const HEARTBEAT_MS = 2000

type RemoteCommand = {
  commandId: string,
  action: string,
  payload?: Record<string, unknown>,
}

type RemoteControlIdentity = {
  remoteControlToken?: string,
  rendererId?: string,
}

let started = false
let clientId = ""
let eventSource: EventSource | null = null
let heartbeatId = 0
let reconnectId = 0
let remoteControlIdentity: RemoteControlIdentity = {}

const getRemoteControlIdentity = () => {
  const identity: RemoteControlIdentity = {}
  try {
    const params = new URLSearchParams(window.location.search)
    const remoteControlToken = params.get("remoteControlToken")
    const rendererId = params.get("rendererId")
    if (remoteControlToken !== null) identity.remoteControlToken = remoteControlToken
    if (rendererId !== null) identity.rendererId = rendererId
  } catch {
    return identity
  }
  return identity
}

const withRemoteControlIdentity = (body: Record<string, unknown>) => {
  return {
    ...body,
    ...remoteControlIdentity,
  }
}

const isRemoteControlEnabled = () => {
  try {
    const params = new URLSearchParams(window.location.search)
    if (params.get("remoteControl") === "1") return true
    return window.localStorage.getItem("remoteControlEnabled") === "true"
  } catch {
    return false
  }
}

const isRemoteServerAvailable = () => {
  return window.location.protocol === "http:" || window.location.protocol === "https:"
}

const decodeBase64 = (value: string) => {
  const binary = atob(value)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes
}

const toByteArray = (value: Uint8Array) => {
  return Array.from(value)
}

const sleep = (ms: number) => {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms)
  })
}

const bytesToBase64 = (bytes: Uint8Array) => {
  let binary = ""
  for (const byte of bytes) {
    binary += String.fromCharCode(byte)
  }
  return btoa(binary)
}

const stringToBase64 = (value: string) => {
  return bytesToBase64(new TextEncoder().encode(value))
}

const base64ToString = (value: string) => {
  const binary = atob(value)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return new TextDecoder().decode(bytes)
}

const getDriveSummary = () => {
  return [0, 1, 2, 3].map((index) => {
    const drive = handleGetDriveProps(index)
    return {
      index: drive.index,
      drive: drive.drive,
      hardDrive: drive.hardDrive,
      filename: drive.filename,
      status: drive.status,
      motorRunning: drive.motorRunning,
      diskHasChanges: drive.diskHasChanges,
      isWriteProtected: drive.isWriteProtected,
      byteLength: drive.diskData.length,
    }
  })
}

let statusSequence = 0

const collectStatus = () => {
  return {
    statusSequence: statusSequence++,
    timestamp: Date.now(),
    machine: {
      runMode: handleGetRunMode(),
      execution: handleGetExecution(),
      speedMode: handleGetSpeedMode(),
      machineName: handleGetMachineName(),
      ramWorksKb: handleGetMemSize(),
      isDebugging: handleGetIsDebugging(),
      showDebugTab: handleGetShowDebugTab(),
      machineState: handleGetState6502(),
      textPage: handleGetTextPageAsString(),
      stackString: handleGetStackString(),
      c800Slot: handleGetC800Slot(),
      softSwitches: handleGetSoftSwitches(),
      memoryAvailable: handleGetRunMode() === RUN_MODE.PAUSED,
    },
    ui: getUIState(),
    drives: getDriveSummary(),
  }
}

const collectMemory = () => {
  return {
    timestamp: Date.now(),
    machineState: handleGetState6502(),
    memoryDump: toByteArray(handleGetCurrentMemory()),
  }
}

const collectBreakpoints = () => {
  return Array.from(handleGetBreakpoints().entries() as IterableIterator<[number, Breakpoint]>).map(([address, breakpoint]) => ({
    ...breakpoint,
    address,
  }))
}

const collectSnapshots = () => {
  const activeIndex = handleGetTempStateIndex()
  return handleGetTimeTravelThumbnails().map((snapshot, index) => ({
    snapshotId: `snap:${index}`,
    index,
    cycleCount: snapshot.s6502.cycleCount,
    label: null,
    thumbnail: snapshot.thumbnail || null,
    active: index === activeIndex,
  }))
}

const waitForSnapshotChange = async (beforeIndex: number, beforeLength: number) => {
  for (let i = 0; i < 40; i++) {
    await sleep(10)
    const snapshots = handleGetTimeTravelThumbnails()
    const currentIndex = handleGetTempStateIndex()
    if (snapshots.length !== beforeLength || currentIndex !== beforeIndex) {
      return collectSnapshots()
    }
  }
  return collectSnapshots()
}

const executeSnapshotAction = async (action: () => void) => {
  const beforeIndex = handleGetTempStateIndex()
  const beforeLength = handleGetTimeTravelThumbnails().length
  action()
  return waitForSnapshotChange(beforeIndex, beforeLength)
}

const exportSaveState = async (withSnapshots: boolean) => {
  const saveState = await new Promise<EmulatorSaveState>((resolve) => {
    handleGetSaveState((state) => {
      resolve(state)
    }, withSnapshots)
  })

  saveState.emulator = {
    name: "Apple2TS Emulator",
    date: new Date(new Date().getTime() - (new Date().getTimezoneOffset() * 60000)).toISOString(),
    version: 2.0,
    ...getUIState(),
    audioEnable: isAudioEnabled(),
    mockingboardMode: getMockingboardMode(),
    speedMode: handleGetSpeedMode(),
    isDebugging: handleGetIsDebugging(),
    runMode: handleGetRunMode(),
    breakpoints: JSON.stringify(Array.from(handleGetBreakpoints().entries())),
  }

  let filename = "apple2ts"
  for (let i = 0; i < 4; i++) {
    const name = handleGetFilename(i)
    if (name) {
      filename = name
      break
    }
  }

  const json = JSON.stringify(saveState, null, 2)
  return {
    filename: `${filename}.a2ts`,
    mimeType: "text/plain",
    dataBase64: stringToBase64(json),
  }
}

const executeStep = async (stepAction: () => void) => {
  const previousCycleCount = handleGetState6502().cycleCount
  stepAction()

  for (let i = 0; i < 40; i++) {
    await sleep(10)
    const currentState = handleGetState6502()
    if (handleGetRunMode() === RUN_MODE.PAUSED && currentState.cycleCount !== previousCycleCount) {
      return collectStatus()
    }
  }

  return collectStatus()
}

const postJson = async (url: string, body: Record<string, unknown>) => {
  await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  })
}

const postState = async () => {
  if (!clientId) return
  await postJson("/api/client/state", withRemoteControlIdentity({
    clientId,
    state: collectStatus(),
  }))
}

setExecutionStateCallback(() => {
  void postState()
})

const postReply = async (commandId: string, ok: boolean, result: unknown, error = "") => {
  if (!clientId) return
  await postJson("/api/client/reply", withRemoteControlIdentity({
    clientId,
    commandId,
    ok,
    result,
    error,
  }))
}

const startHeartbeat = () => {
  if (heartbeatId) return
  heartbeatId = window.setInterval(() => {
    void postState()
  }, HEARTBEAT_MS)
}

const stopHeartbeat = () => {
  if (!heartbeatId) return
  window.clearInterval(heartbeatId)
  heartbeatId = 0
}

const scheduleReconnect = () => {
  if (reconnectId) return
  stopHeartbeat()
  if (eventSource) {
    eventSource.close()
    eventSource = null
  }
  reconnectId = window.setTimeout(() => {
    reconnectId = 0
    void connectToRemoteServer()
  }, CONNECT_RETRY_MS)
}

const connectToRemoteServer = async () => {
  if (!isRemoteServerAvailable()) return

  try {
    remoteControlIdentity = getRemoteControlIdentity()
    const response = await fetch("/api/client/connect", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(withRemoteControlIdentity({
        pathname: window.location.pathname,
        userAgent: navigator.userAgent,
      })),
    })
    if (!response.ok) return

    const payload = await response.json() as { clientId: string }
    clientId = payload.clientId

    if (eventSource) {
      eventSource.close()
    }
    const eventParams = new URLSearchParams({
      clientId,
      ...remoteControlIdentity,
    })
    eventSource = new EventSource(`/api/client/events?${eventParams.toString()}`)
    eventSource.addEventListener("command", (event) => {
      void handleCommand(event as MessageEvent<string>)
    })
    eventSource.onerror = () => {
      scheduleReconnect()
    }

    startHeartbeat()
    await postState()
  } catch {
    scheduleReconnect()
  }
}

export const executeCommand = async (action: string, payload: Record<string, unknown>) => {
  switch (action) {
    case "getStatus":
      return collectStatus()

    case "getMemory":
      return collectMemory()

    case "getMemoryView": {
      return readRemoteMemory(payload)
    }

    case "findMemory": {
      return findRemoteMemory(payload)
    }

    case "compareSessionMemory":
      return requestSessionMemoryComparison({
        snapshotId: String(payload.snapshotId || ""),
        address: Number(payload.address),
        length: Number(payload.length),
        space: (payload.space ?? "main") as MemorySpace,
        ...(payload.auxBank === undefined ? {} : {auxBank: Number(payload.auxBank)}),
        ...(payload.maxChanges === undefined ? {} : {maxChanges: Number(payload.maxChanges)}),
      })

    case "setMemoryWriteWatchpoint":
      return setRemoteMemoryWriteWatchpoint(payload)

    case "clearMemoryWriteWatchpoint":
      return clearRemoteMemoryWriteWatchpoint()

    case "captureScreen":
      return captureRenderedScreen()

    case "setRunMode": {
      const runMode = Number(payload.runMode) as RUN_MODE
      await requestSetRunMode(runMode)
      return collectStatus()
    }

    case "setSpeedMode": {
      const speedMode = Number(payload.speedMode)
      await requestPreferenceSpeedMode(speedMode)
      return collectStatus()
    }

    case "setColorMode":
      setPreferenceColorMode(Number(payload.colorMode))
      return collectStatus()

    case "setMonitorMode":
      setPreferenceMonitorMode(Number(payload.monitorMode))
      return collectStatus()

    case "setMachineName":
      setPreferenceMachineName(payload.machineName as MACHINE_NAME)
      return collectStatus()

    case "setRamWorks":
      setPreferenceRamWorks(Number(payload.size))
      return collectStatus()

    case "setDebug":
      passSetIsDebugging(Boolean(payload.enabled))
      return collectStatus()

    case "setShowDebugTab":
      passSetShowDebugTab(Boolean(payload.enabled))
      return collectStatus()

    case "setKeyboardState":
      await requestKeyboardState(parseRemoteKeyboardState(payload))
      return collectStatus()

    case "sendKeys": {
      const result = await requestKeySequence({
        keys: String(payload.keys ?? ""),
        timeoutMs: Number(payload.timeoutMs),
      })
      return {...result, status: collectStatus()}
    }

    case "runInputSequence": {
      const result = await requestConditionalKeySequence(
        payload as unknown as ConditionalKeySequenceRequest,
      )
      return {...result, status: collectStatus()}
    }

    case "cancelInputSequence": {
      const result = await requestCancelConditionalKeySequence()
      return {...result, status: collectStatus()}
    }

    case "keypress": {
      const value = payload.key
      const keyCode = typeof value === "string" ? value.charCodeAt(0) : Number(value)
      passKeypress(keyCode)
      if (payload.release !== false) {
        passKeyRelease()
      }
      return collectStatus()
    }

    case "pasteText":
      passPasteText(String(payload.text || ""))
      return collectStatus()

    case "appleKey":
      if (payload.side === "left") {
        if (payload.pressed === false) {
          passAppleCommandKeyRelease(true)
        } else {
          passAppleCommandKeyPress(true)
        }
      } else if (payload.pressed === false) {
        passAppleCommandKeyRelease(false)
      } else {
        passAppleCommandKeyPress(false)
      }
      return collectStatus()

    case "mouseEvent":
      passMouseEvent({
        x: Number(payload.x),
        y: Number(payload.y),
        buttons: Number(payload.buttons),
      })
      return collectStatus()

    case "setMemory":
      passSetMemory(Number(payload.address), Number(payload.value))
      return collectStatus()

    case "writeMemory":
      await requestWriteMemory(
        Number(payload.address),
        Uint8Array.from(payload.data as number[]),
      )
      return collectStatus()

    case "setCpuState":
      await requestSetState6502(payload.state as STATE6502)
      return collectStatus()

    case "getBreakpoints":
      return {
        breakpoints: collectBreakpoints(),
      }

    case "setBreakpoints":
      setPreferenceBreakpoints(new BreakpointMap(
        ((payload.breakpoints as Breakpoint[]) || []).map((breakpoint) => [breakpoint.address, breakpoint]),
      ))
      return {
        breakpoints: collectBreakpoints(),
      }

    case "getSnapshots":
      return {
        snapshots: collectSnapshots(),
      }

    case "createSessionSnapshot":
      return {
        snapshot: await requestCreateSessionSnapshot(String(payload.snapshotId || "")),
        status: collectStatus(),
      }

    case "restoreSessionSnapshot":
      return {
        snapshot: await requestRestoreSessionSnapshot(String(payload.snapshotId || "")),
        status: collectStatus(),
      }

    case "createSnapshot":
      return {
        snapshots: await executeSnapshotAction(() => {
          passTimeTravelSnapshot()
        }),
      }

    case "activateSnapshot": {
      const snapshotId = String(payload.snapshotId || "")
      const index = Number(snapshotId.replace(/^snap:/, ""))
      if (!Number.isInteger(index) || index < 0) {
        throw new Error("Invalid snapshot id")
      }
      return {
        snapshots: await executeSnapshotAction(() => {
          passTimeTravelIndex(index)
        }),
      }
    }

    case "stepSnapshotBack":
      if (!handleCanGoBackward()) {
        return {
          snapshots: collectSnapshots(),
        }
      }
      return {
        snapshots: await executeSnapshotAction(() => {
          passGoBackInTime()
        }),
      }

    case "stepSnapshotForward":
      if (!handleCanGoForward()) {
        return {
          snapshots: collectSnapshots(),
        }
      }
      return {
        snapshots: await executeSnapshotAction(() => {
          passGoForwardInTime()
        }),
      }

    case "exportSaveState":
      return exportSaveState(Boolean(payload.includeSnapshots))

    case "importSaveState": {
      const dataBase64 = String(payload.dataBase64 || "")
      if (!dataBase64) {
        throw new Error("dataBase64 is required")
      }
      RestoreSaveState(base64ToString(dataBase64))
      return collectStatus()
    }

    case "setSoftSwitches":
      passSetSoftSwitches((payload.addresses as number[]) || null)
      return collectStatus()

    case "stepInto":
      return executeStep(() => {
        passStepInto()
      })

    case "stepOver":
      return executeStep(() => {
        passStepOver()
      })

    case "stepOut":
      return executeStep(() => {
        passStepOut()
      })

    case "setDriveWriteProtected":
      handleSetDiskWriteProtected(Number(payload.driveIndex), Boolean(payload.isWriteProtected))
      return collectStatus()

    case "mountDisk": {
      const dataBase64 = String(payload.dataBase64 || "")
      const filename = String(payload.filename || "remote.woz")
      const driveIndex = Number(payload.driveIndex || 0)
      const bytes = decodeBase64(dataBase64)
      const arrayBuffer = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength)
      const mountedDrive = await requestMountDiskFromBuffer(
        driveIndex,
        arrayBuffer,
        filename,
        null,
        null,
        undefined,
        true,
      )
      const status = collectStatus()
      return {
        mountedDrive,
        mountedDriveState: status.drives[mountedDrive],
        status,
      }
    }

    case "mountDiskFromUrl": {
      const url = String(payload.url || "")
      const driveIndex = Number(payload.driveIndex || 0)
      if (!url) {
        throw new Error("url is required")
      }
      const mountedDrive = await requestSetDiskFromURL(url, undefined, driveIndex)
      if (mountedDrive === false) {
        throw new Error(`Unable to mount disk from URL: ${url}`)
      }
      passSetRunMode(RUN_MODE.NEED_BOOT)
      const status = collectStatus()
      return {
        mountedDrive,
        mountedDriveState: status.drives[mountedDrive],
        status,
      }
    }

    case "mountBinaryBlock": {
      const dataBase64 = String(payload.dataBase64 || "")
      if (!dataBase64) {
        throw new Error("dataBase64 is required")
      }
      const address = Number(payload.address ?? 0x300)
      const autoRun = payload.autoRun !== false
      const entryAddress = Number(payload.entryAddress ?? address)
      const data = decodeBase64(dataBase64)
      const error = getBinaryLoadError(
        address,
        data,
        autoRun ? entryAddress : null,
        autoRun,
      )
      if (error) throw new Error(error)
      // Deprecated compatibility adapter. Use loadBinary or runBinary.
      if (autoRun) {
        runBinary(address, data, entryAddress)
      } else {
        passSetBinaryBlock(address, data)
      }
      return {
        deprecated: true,
        replacementAction: autoRun ? "runBinary" : "loadBinary",
        status: collectStatus(),
      }
    }

    case "loadBinary": {
      const dataBase64 = String(payload.dataBase64 || "")
      if (!dataBase64) {
        throw new Error("dataBase64 is required")
      }
      const address = Number(payload.address ?? 0x300)
      const data = decodeBase64(dataBase64)
      const error = getBinaryLoadError(address, data, null)
      if (error) throw new Error(error)
      await requestLoadBinary(address, data)
      return {
        accepted: true,
        address,
        size: data.length,
        status: collectStatus(),
      }
    }

    case "runBinary": {
      const dataBase64 = String(payload.dataBase64 || "")
      if (!dataBase64) {
        throw new Error("dataBase64 is required")
      }
      const address = Number(payload.address ?? 0x300)
      const entryAddress = Number(payload.entryAddress ?? address)
      const data = decodeBase64(dataBase64)
      const error = getBinaryLoadError(address, data, entryAddress)
      if (error) throw new Error(error)
      runBinary(address, data, entryAddress)
      return {
        accepted: true,
        address,
        entryAddress,
        size: data.length,
      }
    }

    case "mountBasicText": {
      const text = String(payload.text || "")
      if (!text) {
        throw new Error("text is required")
      }
      const autoRun = payload.autoRun !== false
      let nextText = text
      if (autoRun) {
        const trimmed = text.trim()
        const hasLineNumbers = /^[0-9]/.test(trimmed) || /[\n\r][0-9]/.test(trimmed)
        nextText += hasLineNumbers ? "\nRUN\n" : "\n"
      }
      passPasteText(nextText)
      return {
        status: collectStatus(),
      }
    }

    case "ejectDisk":
      await requestEjectDisk(Number(payload.driveIndex))
      return {
        status: collectStatus(),
      }

    default:
      throw new Error(`Unsupported action '${action}'`)
  }
}

const handleCommand = async (event: MessageEvent<string>) => {
  const command = JSON.parse(event.data) as RemoteCommand
  try {
    const result = await executeCommand(command.action, command.payload || {})
    await postReply(command.commandId, true, result)
    await postState()
  } catch (error) {
    await postReply(
      command.commandId,
      false,
      null,
      error instanceof Error ? error.message : String(error),
    )
  }
}

export const startRemoteControlBridge = () => {
  if (started) return
  if (!isRemoteControlEnabled()) return
  started = true
  void connectToRemoteServer()
}
